import { FC, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";

import MDButtonLoading from "components/MDLoadingButton";
import NegativeImage from "assets/images/NegativeSelfReporting.svg?react";
import PositiveImage from "assets/images/PossitiveSelfReporting.svg?react";
import { styles } from "./SelfReportScreen.styles";
import { ResultActionType } from "types/resultActions";
import { useAppSelector } from "store/hooks";
import { getPendingUpload } from "store/slices/imageRecognition";
import { uploadTestImages } from "services/imageUpload";
import type { ResultStatus } from "types/imageRecognition";
import { SelfReportedResult } from "types/models";
import { logSelfReportAnalytics } from "services/selfReportLogger";
import { sentryLogMessage } from "services/sentryLogger";
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents";
import { getModelMetaDataTags } from "services/models";

interface StepProps { 
  handleUpdateStep: (step: number) => void;
  setResultAction: (action: ResultActionType) => void;
}

const SelfReportScreen: FC<StepProps> = ({ handleUpdateStep, setResultAction }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const useStyles = makeStyles()(() => styles(theme));
  const { classes } = useStyles();
  const pendingUpload = useAppSelector(getPendingUpload);
  
  const [selectedResult, setSelectedResult] = useState<SelfReportedResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitResult = async (selfReportedResult: SelfReportedResult) => {
    if (isSubmitting) {
      return;
    }

    const submissionTimestamp = Date.now();
    setIsSubmitting(true);

    let imageDownloadUrls: Array<string> | undefined;
    let modelResult: ResultStatus = pendingUpload?.modelResult ?? "INVALID";

    if (pendingUpload) {
      try {
        const uploadResult = await uploadTestImages(
          pendingUpload.images,
          pendingUpload.modelResult,
          pendingUpload.metadata,
          pendingUpload.testType,
          pendingUpload.sessionId,
          selfReportedResult,
          pendingUpload.maxQualityImageIndex
        );
        imageDownloadUrls = uploadResult?.imageDownloadUrls;
      } catch (error) {
        console.error('Failed to upload test images:', error);
        logSelfReportAnalytics({
          timestamp: submissionTimestamp,
          selfReportedResult,
          modelResult: pendingUpload.modelResult,
          imageDownloadUrls: ['error'],
        }).catch((analyticsError) => {
          console.error('Failed to log analytics:', analyticsError);
        });
      }
    } else {
      console.warn("SelfReportScreen: No pending upload data found");
    }

    try {
      await logSelfReportAnalytics({
        timestamp: submissionTimestamp,
        selfReportedResult,
        modelResult,
        imageDownloadUrls,
      });
    } catch (error) {
      console.error("SelfReportScreen: logSelfReportAnalytics failed:", error);
    }

    try {
      sentryLogMessage({
        message: "Test completed",
        level: "info",
        tags: {
          component: "SelfReportScreen",
          category: "test_completed",
          event_type: SENTRY_EVENT_TYPES.COMPLETED_TEST,
          ...getModelMetaDataTags(),
        },
        extra: {
          timestamp: new Date().toISOString(),
        },
      });

      sentryLogMessage({
        message: "Result submitted by user",
        level: "info",
        tags: {
          component: "SelfReportScreen",
          category: "result_submitted",
          event_type: SENTRY_EVENT_TYPES.RESULT_SUBMITTED,
          ...getModelMetaDataTags(),
        },
        extra: {
          selfReportedResult,
          modelResult,
          timestamp: new Date().toISOString(),
        },
      });

    } catch (error) {
      console.error("SelfReportScreen: Sentry logging or flush failed:", error);
    }

    setResultAction("DONE");
    handleUpdateStep(6);
  };

  const handleCardClick = (result: SelfReportedResult) => {
    setSelectedResult(result);
  };

  const handleReportIssue = () => {
    setResultAction("SELF_REPORTING_ISSUE");
    handleUpdateStep(6);
  };

  return (
    <div
      className={classes.container}
      aria-busy={isSubmitting}
      data-test-id="self-report-screen"
    >
      {isSubmitting ? (
        <div
          className={classes.submittingOverlay}
          role="status"
          aria-live="polite"
          data-test-id="self-report-submitting-overlay"
        >
          <CircularProgress aria-hidden size={48} />
        </div>
      ) : null}
      <div className={classes.helperText}>
        {t("SelfReportScreen:helperText")}
      </div>
      
      <div className={classes.optionsContainer}>
        <button
          type="button"
          className={`${classes.optionCard} ${
            selectedResult === "negative" ? classes.optionCardSelected : ""
          }`}
          disabled={isSubmitting}
          onClick={() => handleCardClick("negative")}
          data-test-id="self-report-option-negative"
        >
          <div className={classes.optionLinesText}>
            {t("SelfReportScreen:oneLine")}
          </div>
          <div className={classes.optionImage}>
            <NegativeImage />
          </div>
          <div className={classes.optionLabel}>
            {t("SelfReportScreen:negative")}
          </div>
        </button>
        <button
          type="button"
          className={`${classes.optionCard} ${
            selectedResult === "positive" ? classes.optionCardSelected : ""
          }`}
          disabled={isSubmitting}
          onClick={() => handleCardClick("positive")}
          data-test-id="self-report-option-positive"
        >
          <div className={classes.optionLinesText}>
            {t("SelfReportScreen:twoLines")}
          </div>
          <div className={classes.optionImage}>
            <PositiveImage />
          </div>
          <div className={classes.optionLabel}>
            {t("SelfReportScreen:positive")}
          </div>
        </button>
      </div>

      <div className={classes.buttonsSection}>
        <button
          type="button"
          className={classes.link}
          disabled={isSubmitting}
          onClick={handleReportIssue}
          data-test-id="self-report-report-issue"
        >
          {t("SelfReportScreen:reportIssue")}
        </button>
        <MDButtonLoading
          style={{ width: theme.width.full, height: theme.spacing.spacing28 }}
          color={"primary"}
          variant="contained"
          type="submit"
          loading={isSubmitting}
          disabled={selectedResult === null}
          data-test-id="self-report-submit-button"
          onClick={() => {
            if (selectedResult !== null) {
              handleSubmitResult(selectedResult);
            }
          }}
        >
          {t("SelfReportScreen:submitButton")}
        </MDButtonLoading>
      </div>
    </div>
  );
};

export default SelfReportScreen;

